// src/pages/IntegrationDetailPage/IntegrationSettingsSidebar.js
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Flex, H3, P } from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import Archive from '@decisiv/iconix/lib/components/Archive';
import {
  CustomArchiveBox,
  CustomAudienceInfo,
  CustomIntegrationSettingsInfo,
} from './style';
import { SectionDivider } from './StyledComponents';

const IntegrationSettingsSidebar = ({
  integration,
  audience,
  serviceNetwork,
  triggerTypes,
  action = null,
  applicationEvents,
  authorizeOn,
  loadingState,
  integrationAction,
  integrationConfigured,
  edit,
  hasGlobalSettings,
  handleToggleArchiveModal,
  handleToggleIntegrationStatus,
  handleEditIntegration,
  hasConfiguredIntegrations,
}) => (
  <>
    <CustomIntegrationSettingsInfo>
      {authorizeOn() && (
        <div>
          <H3>
            {integration.attributes.name} <Trans>Integration</Trans>
          </H3>
          <Flex marginTop={1} marginBottom={1}>
            <Trans>Category</Trans>
            <P color="alaskanHusky" weight="medium" size="small">
              &nbsp;{integration.attributes.category}
            </P>
          </Flex>
          <Flex flexDirection="column">
            <Flex marginBottom={1}>
              {!integration.attributes.enabled && (
                <P color="alaskanHusky" weight="medium" size="small">
                  <Trans>
                    Once activated, it will become available for all enabled
                    locations.
                  </Trans>
                </P>
              )}
            </Flex>
            <Button
              loading={loadingState}
              margin="10px"
              text={integrationAction}
              disabled={
                !integrationConfigured() ||
                (!edit && hasGlobalSettings) ||
                !hasConfiguredIntegrations
              }
              onClick={() => handleToggleIntegrationStatus('enabled')}
            />
            <SectionDivider />
            <Flex marginTop={1} marginBottom={1}>
              <P color="alaskanHusky" weight="medium" size="small">
                <Trans>
                  Editing an Integration could lead to changes required in the
                  global settings.
                </Trans>
              </P>
            </Flex>
            <Button
              margin="10px"
              text={t`Edit Integration`}
              kind="secondary"
              size="small"
              onClick={handleEditIntegration}
            />
          </Flex>
        </div>
      )}
    </CustomIntegrationSettingsInfo>

    <CustomAudienceInfo>
      <H3>
        <Trans>Audience</Trans>
      </H3>
      {audience?.sort()?.map((audienceInput) => (
        <p key={audienceInput}>{audienceInput}</p>
      ))}
    </CustomAudienceInfo>

    <CustomAudienceInfo>
      <H3>
        <Trans>Service Network</Trans>
      </H3>
      {serviceNetwork?.sort()?.map((serviceNetworkInput) => (
        <p key={serviceNetworkInput}>{serviceNetworkInput}</p>
      ))}
    </CustomAudienceInfo>

    <CustomAudienceInfo>
      <H3>
        <Trans>Trigger</Trans>
      </H3>
      {triggerTypes?.sort()?.map((trigger) => (
        <p key={trigger}>{trigger}</p>
      ))}
    </CustomAudienceInfo>

    {action && (
      <CustomAudienceInfo>
        <H3>
          <Trans>Action</Trans>
        </H3>
        <p key={action}>{action}</p>
      </CustomAudienceInfo>
    )}

    {applicationEvents?.length > 0 && (
      <CustomAudienceInfo>
        <H3>
          <Trans>Application Events</Trans>
        </H3>
        {applicationEvents?.sort()?.map((event) => (
          <p key={event}>{event}</p>
        ))}
      </CustomAudienceInfo>
    )}

    {authorizeOn() && (
      <CustomArchiveBox marginTop={1}>
        <Flex flexDirection="column">
          <P color="alaskanHusky" weight="medium" size="small" marginBottom={1}>
            <Trans>
              Archived integrations are no longer visible from the homepage.
            </Trans>
          </P>
          <Button
            margin="10px"
            text={t`Archive`}
            name="archive"
            kind="secondary"
            size="small"
            icon={Archive}
            onClick={handleToggleArchiveModal}
          />
        </Flex>
      </CustomArchiveBox>
    )}
  </>
);

IntegrationSettingsSidebar.propTypes = {
  integration: PropTypes.shape({
    attributes: PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
      enabled: PropTypes.bool,
    }),
  }).isRequired,
  audience: PropTypes.arrayOf(PropTypes.string).isRequired,
  serviceNetwork: PropTypes.arrayOf(PropTypes.string).isRequired,
  triggerTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  action: PropTypes.string,
  applicationEvents: PropTypes.arrayOf(PropTypes.string).isRequired,
  authorizeOn: PropTypes.func.isRequired,
  loadingState: PropTypes.bool.isRequired,
  integrationAction: PropTypes.string.isRequired,
  integrationConfigured: PropTypes.func.isRequired,
  edit: PropTypes.bool.isRequired,
  hasGlobalSettings: PropTypes.bool.isRequired,
  handleToggleArchiveModal: PropTypes.func.isRequired,
  handleToggleIntegrationStatus: PropTypes.func.isRequired,
  handleEditIntegration: PropTypes.func.isRequired,
  hasConfiguredIntegrations: PropTypes.bool.isRequired,
};
export default IntegrationSettingsSidebar;
